import { Component, OnDestroy, OnInit } from '@angular/core';
import { lastValueFrom, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { IGroupedNotifications, INotification, INotificationsCount } from '../../../models/notifications';
import { SocketService } from '../../../services/socket.service';
import { PanelHeaderTitle, SidePanelService } from '../../../services/side-panel.service';
import { catchError, switchMap } from 'rxjs/operators';
import { NotificationsService } from '../../../services/notifications.service';
import { SnackBarService } from '../../../services/snack-bar.service';
import { RequestParams } from '../../../models/requestParams';
import { ITableData } from '../../../models/table';
import { TranslateService } from '@ngx-translate/core';
import { Utils } from '../../../services/utils.service';
import { TableService } from '../../../services/table.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../../modals/confirm-modal/models/confirmModalAnswer';
import { Filter, FilterID, FilterItem, FilterType, setDefaults } from '../../filters-panel/models/filter';
import { FiltersService } from '../../filters-panel/filters.service';
import { DateRange } from '@angular/material/datepicker';
import { DateSelectingStrategy } from '../../filters-panel/models/strategy';
import { FiltersPanelService } from '../../filters-panel/filters-panel.service';
import Moment from 'moment/moment';
import { SessionService } from '../../../services/session.service';

import { SHARED_MODULES } from '../../../../shared.';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FiltersPanelComponent } from '../../filters-panel/filters-panel.component';
import { LoaderComponent } from '../../loader/loader.component';
import { PhIconComponent } from '../../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLoaderComponent } from '../../ph-loader/ph-loader.component';
import { PhNotificationComponent } from '../ph-notification/ph-notification.component';
import { PhNotificationsDetailedPanelComponent } from '../ph-notifications-detailed-panel/ph-notifications-detailed-panel.component';
import { SidePanelWrapperComponent } from '../../side-panel-wrapper/side-panel-wrapper.component';
import { DateFormatService } from '../../../services/date-format.service';

@Component({
	selector: 'ph-notifications-panel',
	templateUrl: './ph-notifications-panel.component.html',
	styleUrls: ['./ph-notifications-panel.component.scss'],
	imports: [
		SHARED_MODULES,
		InfiniteScrollDirective,
		FaIconComponent,
		FiltersPanelComponent,
		LoaderComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhLoaderComponent,
		PhNotificationComponent,
		PhNotificationsDetailedPanelComponent,
		SidePanelWrapperComponent
	],
	providers: [FiltersPanelService],
	standalone: true
})
export class PhNotificationsPanelComponent implements OnInit, OnDestroy {

	private _allRequestParams: RequestParams;
	private _archivedRequestParams: RequestParams;
	private _defaultDates: { from: string; to: string };
	private _filters: Array<Filter> = [];
	private _subscriptions: Array<Subscription>;
	private _unreadRequestParams: RequestParams;
	private loadAll$: ReplaySubject<void> = new ReplaySubject<void>();
	private loadArchived$: ReplaySubject<void> = new ReplaySubject<void>();
	private loadUnread$: ReplaySubject<void> = new ReplaySubject<void>();
	private updateAll$: Subject<void> = new Subject<void>();
	private updateArchived$: Subject<void> = new Subject<void>();
	private updateUnread$: Subject<void> = new Subject<void>();

	public allLoading: boolean = true;
	public allNotifications: ITableData<INotification>;
	public archivedLoading: boolean = true;
	public archivedNotifications: ITableData<INotification>;
	public count: INotificationsCount;
	public countFiltered: INotificationsCount;
	public deleteLoader: boolean = false;
	public groupedAllNotifications: Array<IGroupedNotifications> = [];
	public groupedArchivedNotifications: Array<IGroupedNotifications> = [];
	public groupedUnreadNotifications: Array<IGroupedNotifications> = [];
	public newAllCount: number = 0;
	public newArchivedCount: number = 0;
	public newUnreadCount: number = 0;
	public openedDetailsPanel: boolean = false;
	public panelNotification: INotification;
	public readLoader: boolean = false;
	public selectedTabIndex: number;
	public someFiltersApplied: boolean = false;
	public unreadLoading: boolean = true;
	public unreadNotifications: ITableData<INotification>;

	constructor(
		private dialog: MatDialog,
		private translate: TranslateService,
		private socketService: SocketService,
		private sessionService: SessionService,
		private sidePanelService: SidePanelService,
		private notificationsService: NotificationsService,
		private snackBarService: SnackBarService,
		private tableService: TableService,
		private filtersPanelService: FiltersPanelService,
		private filtersService: FiltersService,
		private dateFormatService: DateFormatService
	) {
		this.selectedTabIndex = !this.socketService.notificationsCount.value?.unread ? 0 : 1;
		this._defaultDates = {
			from: Moment(this.sessionService.defaults.date_from).format('YYYY-MM-DD'),
			to: Moment().format('YYYY-MM-DD')
		};
		this.count = { total: 0, unread: 0, archived: 0 };
		this.countFiltered = { total: 0, unread: 0, archived: 0 };
		this._allRequestParams = new RequestParams({ page_size: 30, archived: false, date_from: this._defaultDates.from, date_to: this._defaultDates.to });
		this._archivedRequestParams = new RequestParams({ page_size: 30, archived: true, date_from: this._defaultDates.from, date_to: this._defaultDates.to });
		this._unreadRequestParams = new RequestParams({ page_size: 30, read: false, archived: false, date_from: this._defaultDates.from, date_to: this._defaultDates.to });
	}

	ngOnInit(): void {
		this.sidePanelService.title = PanelHeaderTitle.Notifications;
		this.sidePanelService.showButtons = false;

		this._subscriptions = [
			this.socketService.notificationsCount.subscribe((count: INotificationsCount) => {
				this.count = count;
				if (this.someFiltersApplied) {
					this.allLoading = true;
					this.unreadLoading = true;
					this.archivedLoading = true;
					this.notificationsService
						.count(this._allRequestParams.getParams(null, ['page_size', 'archived']))
						.subscribe((count: INotificationsCount) => {
							if (!count) return;
							this.countFiltered = count;
							if (this.allNotifications) {
								this.allLoading = false;
								this.allNotifications.count = count.total;
								this.allNotifications.has_data = !!count.total;
							}
							if (this.unreadNotifications) {
								this.unreadLoading = false;
								this.unreadNotifications.count = count.unread;
								this.unreadNotifications.has_data = !!count.unread;
							}
							if (this.archivedNotifications) {
								this.archivedLoading = false;
								this.archivedNotifications.count = count.archived;
								this.archivedNotifications.has_data = !!count.archived;
							}
						});
				} else {
					if (this.allNotifications) {
						if (count.total > this.allNotifications.count) {
							this.newAllCount += count.total - this.allNotifications.count;
						}
						this.allNotifications.count = count.total;
						this.allNotifications.has_data = !!count.total;
					}
					if (this.unreadNotifications) {
						if (count.unread > this.unreadNotifications.count) {
							this.newUnreadCount += count.unread - this.unreadNotifications.count;
						}
						this.unreadNotifications.count = count.unread;
						this.unreadNotifications.has_data = !!count.unread;
					}
					if (this.archivedNotifications) {
						if (count.archived > this.archivedNotifications.count) {
							this.newArchivedCount += count.archived - this.archivedNotifications.count;
						}
						this.archivedNotifications.count = count.archived;
						this.archivedNotifications.has_data = !!count.archived;
					}
				}
			}),

			this.socketService.notificationsRead.subscribe((data: { ids: Array<number> }) => {
				if (!this.unreadNotifications?.has_data) return;
				const notifications: Array<INotification> = this.unreadNotifications.results.dataSource;
				this.unreadNotifications.results.dataSource = notifications.filter((notification: INotification) => {
					return !data?.ids?.includes(notification.id);
				});
				if (this.unreadNotifications.next && this.unreadNotifications.results.dataSource.length < 20) {
					this.updateUnread$.next();
				}
				this.groupedUnreadNotifications = this.groupNotifications(this.unreadNotifications.results.dataSource);
			}),

			this.socketService.notificationsDeleted.subscribe((data: { ids: Array<number> }) => {
				if (!this.archivedNotifications?.has_data) return;
				const notifications: Array<INotification> = this.archivedNotifications.results.dataSource;
				this.archivedNotifications.results.dataSource = notifications.filter((notification: INotification) => {
					return !data?.ids?.includes(notification.id);
				});
				if (this.archivedNotifications.next && this.archivedNotifications.results.dataSource.length < 20) {
					this.updateArchived$.next();
				}
				this.groupedArchivedNotifications = this.groupNotifications(this.archivedNotifications.results.dataSource);
			}),

			this.socketService.notificationsArchived.subscribe((data: { ids: Array<number> }) => {
				if (this.unreadNotifications?.has_data) {
					const notifications: Array<INotification> = this.unreadNotifications.results.dataSource;
					this.unreadNotifications.results.dataSource = notifications.filter((notification: INotification) => {
						return !data?.ids?.includes(notification.id);
					});
					if (this.unreadNotifications.next && this.unreadNotifications.results.dataSource.length < 20) {
						this.updateUnread$.next();
					}
					this.groupedUnreadNotifications = this.groupNotifications(this.unreadNotifications.results.dataSource);
				}

				if (this.allNotifications?.has_data) {
					const notifications: Array<INotification> = this.allNotifications.results.dataSource;
					this.allNotifications.results.dataSource = notifications.filter((notification: INotification) => {
						return !data?.ids?.includes(notification.id);
					});
					if (this.allNotifications.next && this.allNotifications.results.dataSource.length < 20) {
						this.updateAll$.next();
					}
					this.groupedAllNotifications = this.groupNotifications(this.allNotifications.results.dataSource);
				}
			}),

			this.socketService.notificationsUnArchived.subscribe((data: { ids: Array<number> }) => {
				if (this.archivedNotifications?.has_data) {
					const notifications: Array<INotification> = this.archivedNotifications.results.dataSource;
					this.archivedNotifications.results.dataSource = notifications.filter((notification: INotification) => {
						return !data?.ids?.includes(notification.id);
					});
					if (this.archivedNotifications.next && this.archivedNotifications.results.dataSource.length < 20) {
						this.updateUnread$.next();
					}
					this.groupedArchivedNotifications = this.groupNotifications(this.archivedNotifications.results.dataSource);
				}
			}),

			this.loadAll$
				.pipe(
					switchMap(() => this.notificationsService
						.getList(this._allRequestParams.getParams())
						.pipe(
							catchError(() => {
								this.snackBarService.setErrorNotification();
								return of(null);
							})
						)
					)
				)
				.subscribe((response: ITableData<INotification>) => {
					this.allLoading = false;
					this.newAllCount = 0;
					if (response) {
						this.allNotifications = response;
						this.groupedAllNotifications = this.groupNotifications(this.allNotifications.results.dataSource);
						return;
					}
					if (!this.allNotifications) {
						this.allNotifications = {
							count: 0,
							has_data: false,
							next: null,
							results: null
						}
					}
				}),

			this.loadArchived$
				.pipe(
					switchMap(() => this.notificationsService
						.getList(this._archivedRequestParams.getParams())
						.pipe(
							catchError(() => {
								this.snackBarService.setErrorNotification();
								return of(null);
							})
						)
					)
				)
				.subscribe((response: ITableData<INotification>) => {
					this.archivedLoading = false;
					this.newArchivedCount = 0;
					if (response) {
						this.archivedNotifications = response;
						this.groupedArchivedNotifications = this.groupNotifications(this.archivedNotifications.results.dataSource);
						return;
					}
					if (!this.archivedNotifications) {
						this.archivedNotifications = {
							count: 0,
							has_data: false,
							next: null,
							results: null
						}
					}
				}),

			this.loadUnread$
				.pipe(
					switchMap(() => this.notificationsService
						.getList(this._unreadRequestParams.getParams())
						.pipe(
							catchError(() => {
								this.snackBarService.setErrorNotification();
								return of(null);
							})
						)
					)
				)
				.subscribe((response: ITableData<INotification>) => {
					this.unreadLoading = false;
					this.newUnreadCount = 0;
					if (response) {
						this.unreadNotifications = response;
						this.groupedUnreadNotifications = this.groupNotifications(this.unreadNotifications.results.dataSource);
						return;
					}
					if (!this.unreadNotifications) {
						this.unreadNotifications = {
							count: 0,
							has_data: false,
							next: null,
							results: null
						}
					}
				}),

			this.updateAll$
				.pipe(
					switchMap(() => {
						return this.tableService.getNextTablePage<ITableData<INotification>, any>('GET', this.allNotifications.next);
					}),
					catchError(() => {
						this.snackBarService.setErrorNotification();
						return of(null);
					})
				)
				.subscribe((response: ITableData<INotification>) => {
					if (!response?.results?.dataSource?.length) return;
					const data: Array<INotification> = this.allNotifications.results.dataSource;
					data.push(...response.results.dataSource);
					this.allNotifications = response;
					this.allNotifications.results.dataSource = data;
					this.groupedAllNotifications = this.groupNotifications(this.allNotifications.results.dataSource);
				}),

			this.updateArchived$
				.pipe(
					switchMap(() => {
						return this.tableService.getNextTablePage<ITableData<INotification>, any>('GET', this.archivedNotifications.next);
					}),
					catchError(() => {
						this.snackBarService.setErrorNotification();
						return of(null);
					})
				)
				.subscribe((response: ITableData<INotification>) => {
					if (!response?.results?.dataSource?.length) return;
					const data: Array<INotification> = this.archivedNotifications.results.dataSource;
					data.push(...response.results.dataSource);
					this.archivedNotifications = response;
					this.archivedNotifications.results.dataSource = data;
					this.groupedArchivedNotifications = this.groupNotifications(this.archivedNotifications.results.dataSource);
				}),

			this.updateUnread$
				.pipe(
					switchMap(() => {
						return this.tableService.getNextTablePage<ITableData<INotification>, any>('GET', this.unreadNotifications.next);
					}),
					catchError(() => {
						this.snackBarService.setErrorNotification();
						return of(null);
					})
				)
				.subscribe((response: ITableData<INotification>) => {
					if (!response?.results?.dataSource) return;
					const data: Array<INotification> = this.unreadNotifications.results.dataSource;
					data.push(...response.results.dataSource);
					this.unreadNotifications = response;
					this.unreadNotifications.results.dataSource = data;
					this.groupedUnreadNotifications = this.groupNotifications(this.unreadNotifications.results.dataSource);
				})
		];

		this.tabIndexChange(this.selectedTabIndex);
	}

	private applyFilters(): void {
		if (this.allNotifications) this.loadAll();
		if (this.unreadNotifications) this.loadUnread();
		if (this.archivedNotifications) this.loadArchived();
		this.notificationsService
			.count(this._allRequestParams.getParams(null, ['page_size', 'archived']))
			.subscribe((count: INotificationsCount) => this.countFiltered = count);
	}

	private archive(): void {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'NOTIFICATIONS.ARCHIVE_ALL_WARNING',
				confirmLabel: 'YES',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});
		dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
			if (result.answer) {
				this.deleteLoader = true;
				let params: any;
				switch (this.selectedTabIndex) {
					case 0: params = this._allRequestParams.getParams(null, ['page_size']); break;
					case 1: params = this._unreadRequestParams.getParams(null, ['page_size']); break;
					default: break;
				}
				this.notificationsService.archive(params).subscribe({
					error: () => {
						this.snackBarService.setErrorNotification();
						this.deleteLoader = false;
					},
					complete: () => this.deleteLoader = false
				});
			}
		});
	}

	public clearFilters(): void {
		this.filtersPanelService.setApplyPanel(false);
	}

	private delete(): void {
		const dialogRef = this.dialog.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
			width: '574px',
			autoFocus: false,
			backdropClass: 'back-drop',
			data: {
				label: 'NOTIFICATIONS.DELETE_ALL_WARNING',
				confirmLabel: 'YES',
				cancelLabel: 'NO',
				twoAnswers: true
			}
		});

		dialogRef.afterClosed().subscribe((result: IConfirmModalAnswer) => {
			if (result.answer) {
				this.deleteLoader = true;
				const params = this._archivedRequestParams.getParams(null, ['page_size']);
				this.notificationsService.delete(params).subscribe({
					error: () => {
						this.snackBarService.setErrorNotification();
						this.deleteLoader = false;
					},
					complete: () => this.deleteLoader = false
				});
			}
		});
	}

	private groupNotifications(notifications: Array<INotification>): Array<IGroupedNotifications> {
		const today: Date = new Date();
		const yesterday: Date = new Date();
		yesterday.setDate(today.getDate() - 1);

		const translates: Record<string, string> = this.translate.instant(['TODAY', 'YESTERDAY']);
		const grouped: { [key: string]: Array<INotification> } = notifications
			.sort((a: INotification, b: INotification) =>
				new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
			)
			.reduce((acc: { [key: string]: Array<INotification> } , notification: INotification) => {
				const date: string = new Date(notification.created_at).toISOString().split('T')[0];
				const notificationDate: Date = new Date(date);

				let dateLabel: string;
				if (Utils.isSameDay(notificationDate, today)) {
					dateLabel = translates['TODAY'];
				} else if (Utils.isSameDay(notificationDate, yesterday)) {
					dateLabel = translates['YESTERDAY'];
				} else {
					dateLabel = this.dateFormatService.formatDate(notificationDate, 'date');
				}

				if (!acc[dateLabel]) acc[dateLabel] = [];
				acc[dateLabel].push(notification);
				return acc;
			}, {} as { [key: string]: Array<INotification> });

		return Object.keys(grouped).map((dateLabel: string) => ({
			dateLabel,
			notifications: grouped[dateLabel]
		}));
	}

	public loadAll(): void {
		this.allLoading = true;
		this.loadAll$.next();
	}

	public loadArchived(): void {
		this.archivedLoading = true;
		this.loadArchived$.next();
	}

	public loadUnread(): void {
		this.unreadLoading = true;
		this.loadUnread$.next();
	}

	public loadNextAll(): void {
		if (this.allNotifications.next) this.updateAll$.next();
	}

	public loadNextArchived(): void {
		if (this.archivedNotifications.next) this.updateArchived$.next();
	}

	public loadNextUnread(): void {
		if (this.unreadNotifications.next) this.updateUnread$.next();
	}

	public openDetailsPanel(notification: INotification): void {
		this.panelNotification = notification;
		this.openedDetailsPanel = true;
	}

	public onDelete(): void {
		if (this.selectedTabIndex === 2) {
			this.delete();
		} else {
			this.archive();
		}
	}

	public onRead(): void {
		this.readLoader = true;
		this.notificationsService.read(this._allRequestParams.getParams(null, ['page_size'])).subscribe({
			error: () => {
				this.snackBarService.setErrorNotification();
				this.readLoader = false;
			},
			complete: () => this.readLoader = false
		});
	}

	public async openFiltersPanel(): Promise<void> {
		this.filtersPanelService.setShowPanel(true);
		if (this._filters.length) {
			setDefaults(this._filters, (param: string) => this._allRequestParams.getParam(param));
			this.filtersPanelService.setFilters(this._filters);
			return;
		}

		let originalTypes: Array<FilterItem>;
		try {
			originalTypes = await lastValueFrom(this.filtersService.getNotificationsTypes());
		} catch (error) {
			this.snackBarService.setErrorNotification();
			originalTypes = [];
		}

		this._filters.push({
			id: FilterID.Date,
			type: FilterType.Date,
			param: 'date',
			label: 'PERIOD',
			displayOrder: 1,
			data: [
				{
					id: 'period',
					value: new DateRange<string>(this._defaultDates.from, this._defaultDates.to),
					label: 'PERIOD'
				}
			],
			selectingStrategy: new DateSelectingStrategy([
				{
					id: 'period',
					value: new DateRange<string>(
						this._allRequestParams.getParam('date_from'),
						this._allRequestParams.getParam('date_to')
					),
					label: 'PERIOD'
				}
			])
		});


		if (originalTypes.length) {
			this._filters.push(this.filtersService.getNotificationsTypesFilter(2, originalTypes));
		}

		setDefaults(this._filters, (param: string) => this._allRequestParams.getParam(param));
		this.filtersPanelService.setFilters(this._filters);
		this._subscriptions.push(this.filtersPanelService.getApplyPanel().subscribe((apply: boolean) => {
			this.someFiltersApplied = false;

			this._filters.forEach((filter: Filter) => {
				if (filter.param === 'date') {
					const dates = (filter.selectingStrategy as DateSelectingStrategy).getSelected();
					let date_from: string = this.sessionService.formatDate(dates.period.start, 'yyyy-MM-dd'),
						date_to: string = this.sessionService.formatDate(dates.period.end, 'yyyy-MM-dd');

					if (
						(date_from && date_from !== this._defaultDates.from) ||
						(date_to && date_to !== this._defaultDates.to)
					) {
						this.someFiltersApplied = true;
					}

					this._allRequestParams.updateParams({
						date_from: date_from,
						date_to: date_to
					});
					this._archivedRequestParams.updateParams({
						date_from: date_from,
						date_to: date_to
					});
					this._unreadRequestParams.updateParams({
						date_from: date_from,
						date_to: date_to
					});
				} else {
					if (apply) {
						const updateParam = {};
						updateParam[filter.param] = filter.selectingStrategy.getSelected();
						this._allRequestParams.updateParams(updateParam);
						this._archivedRequestParams.updateParams(updateParam);
						this._unreadRequestParams.updateParams(updateParam);
						if (filter.selectingStrategy.isSelected()) this.someFiltersApplied = true;
					} else {
						const updateParam = {};
						updateParam[filter.param] = null;
						this._allRequestParams.updateParams(updateParam);
						this._archivedRequestParams.updateParams(updateParam);
						this._unreadRequestParams.updateParams(updateParam);
					}
				}
			});

			this.applyFilters();
		}));
		this._subscriptions.push(this.filtersPanelService.getShowPanel().subscribe((show: boolean) => {
			if (!show) {
				setDefaults(this._filters, (param: string) => this._allRequestParams.getParam(param));
				this._filters.forEach((filter: Filter) => {
					if (filter.type == FilterType.Date) {
						filter.selectingStrategy = new DateSelectingStrategy([
							{
								id: 'period',
								value: new DateRange<string>(
									this._allRequestParams.getParam('date_from'),
									this._allRequestParams.getParam('date_to')
								),
								label: 'PERIOD'
							}
						]);
					}
				});
			}
		}));
	}

	public scrollToDay(index: number): void {
		const element: HTMLElement = document.getElementById('day-' + index);
		if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}

	public tabIndexChange(index: number): void {
		switch (index) {
			case 0: if (!this.allNotifications) this.loadAll(); break;
			case 1: if (!this.unreadNotifications) this.loadUnread(); break;
			case 2: if (!this.archivedNotifications) this.loadArchived(); break;
		}
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((s: Subscription) => s.unsubscribe());
	}
}
